/**
 * A cache that remembers answers and forgets failures.
 *
 * Every lookup cache in weatherService stored `null` for "no answer" and kept
 * it for the life of the page. That conflates two different nulls. A city that
 * genuinely cannot be geocoded should be remembered, or every render asks
 * again. A city whose lookup failed — offline for a moment, a rate-limit blip,
 * a transient REQUEST_DENIED — should not be: one bad second permanently
 * removed the weather for that destination, and only a reload brought it back.
 *
 * Rather than teach every fetcher to classify its own failures, a null is kept
 * only briefly. A real negative costs one cheap re-check a minute; a transient
 * one clears itself. It's the same lesson as the Firestore listeners: a failure
 * that lasts until reload is the worst kind, because nothing tells you.
 */

const MISS_TTL_MS = 60_000;

export class MissCache<T> {
  private entries = new Map<string, { value: T | null; at: number }>();

  constructor(private readonly missTtlMs = MISS_TTL_MS, private readonly now = () => Date.now()) {}

  /** The cached value, or undefined when there is nothing usable to return. */
  get(key: string): T | null | undefined {
    const entry = this.entries.get(key);
    if (!entry) return undefined;
    if (entry.value === null && this.now() - entry.at >= this.missTtlMs) {
      this.entries.delete(key);
      return undefined;
    }
    return entry.value;
  }

  has(key: string): boolean {
    return this.get(key) !== undefined;
  }

  set(key: string, value: T | null): void {
    this.entries.set(key, { value, at: this.now() });
  }
}
